import React from 'react';
import classNames from 'classnames';

import { getErrorMessage } from '~libs/axiosWrapper';

import { ErrorResponse, Spinner } from '~components';
import Icon, {
  faArrowRightArrowLeft,
  faFileInvoice,
  faInfoCircle,
  faLink,
  faMoneyBill,
  faTriangleExclamation,
} from '~components/icon';
import StatisticStack, { StatisticConfig } from '~components/visualisation/statistic/Statistics';

import formatDate from '~support-at-home/lib/formatDate';

import { useGetSahDashboardQuery } from '~support-at-home/actions/sahDashboard';

/** Statistic icon colours aligned with the project SCSS theme variables */
const ICON_COLOURS = {
  primary: '#00b4ae',
  secondary: '#1e384c',
  green: '#008000',
  warning: '#faa51a',
  onHold: '#7b90d6',
  red: '#ff0000',
} as const;

const SahApiDashboard: React.FC = () => {
  const { data, isLoading, isFetching, isError, error } = useGetSahDashboardQuery();

  if (isLoading) {
    return <Spinner loading className="p-5" />;
  }

  if (isError) {
    return <ErrorResponse message={`Failed to load dashboard data - ${getErrorMessage(error)}`} dataCy="sah-api-dashboard-error" />;
  }

  if (!data) {
    return (
      <div className="sah-api-dashboard">
        <div className="empty" role="status">
          <Icon icon={faInfoCircle} />
          No dashboard data available.
        </div>
      </div>
    );
  }

  const claimsTotal = data.claims.in_flight.reduce((sum, entry) => sum + entry.count, 0);

  // `is_connected` and `last_status` are independent facts from different tables: the first is
  // whether the PRODA device is activated (company_proda_connections.status), the second is whether
  // the last actual API run succeeded (company_external_api_statuses.last_status). A provider can be
  // activated and still have a failed sync, so the tile's Connected/Disconnected value cannot stand
  // in for run health on the one panel whose job is reporting it.
  const lastRunFailed = data.connection.last_status === 'error';

  const statistics: StatisticConfig[] = [
    {
      title: 'PRODA Connection',
      type: 'value',
      value: data.connection.is_connected ? 'Connected' : 'Disconnected',
      icon: faLink,
      iconColor: data.connection.is_connected ? ICON_COLOURS.green : ICON_COLOURS.red,
      // Pill on FAILURE ONLY. A green "success" chip beside a tile already reading "Connected" is
      // duplicate signal (Cole, TAV-2603 review), and `null` means never run rather than failed, so
      // neither earns a pill. An errored run has nowhere else on this panel to surface.
      ...(lastRunFailed
        ? { pill: { label: 'Last run failed', color: ICON_COLOURS.red, icon: <Icon icon={faTriangleExclamation} /> } }
        : {}),
      dataCy: 'sah-api-dashboard-connection',
    },
    {
      title: 'Active Budgets',
      type: 'value',
      value: data.budgets.active_count,
      icon: faMoneyBill,
      iconColor: ICON_COLOURS.primary,
      breakdown: [{ label: 'Last synced', value: formatDate(data.budgets.last_synced_at) }],
      dataCy: 'sah-api-dashboard-budgets',
    },
    {
      title: 'Claims In Flight',
      type: 'value',
      value: claimsTotal,
      icon: faFileInvoice,
      iconColor: ICON_COLOURS.secondary,
      // Only the 3 real statuses the API returns — never a hardcoded 4th "submitting" tile.
      breakdown: data.claims.in_flight.map((entry) => ({ label: entry.name, value: String(entry.count) })),
      dataCy: 'sah-api-dashboard-claims',
    },
    {
      title: 'Auto-Reconciled This Month',
      type: 'value',
      value: data.reconciliations.auto_reconciled_this_month,
      icon: faArrowRightArrowLeft,
      iconColor: ICON_COLOURS.onHold,
      // Both figures are claim-item counts so they compare like with like, but the windows differ:
      // the headline is this month, the breakdown is a backlog with no window. Labelled explicitly
      // so the pair cannot be read as a month-on-month comparison.
      breakdown: [
        { label: 'Outstanding unmatched (all time)', value: String(data.reconciliations.outstanding_unmatched) },
      ],
      dataCy: 'sah-api-dashboard-reconciliations',
    },
    {
      title: 'Amendments Caught This Month',
      type: 'value',
      value: data.amendments.caught_this_month,
      icon: faTriangleExclamation,
      iconColor: ICON_COLOURS.warning,
      dataCy: 'sah-api-dashboard-amendments',
    },
  ];

  return (
    <div
      className={classNames('sah-api-dashboard', { fetching: isFetching })}
      data-cy="sah-api-dashboard"
      aria-busy={isFetching}
    >
      <StatisticStack statistics={statistics} />
    </div>
  );
};

export default SahApiDashboard;
